import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import MetricsDisplay from './components/MetricsDisplay';
import ProjectPreview from './components/ProjectPreview';
import TechStack from './components/TechStack';
import CallToAction from './components/CallToAction';
import Icon from '../../components/AppIcon';

const NexusCommandCenter = () => {
  useEffect(() => {
    // Set page title
    document.title = 'CyberDev Nexus - Command Center | Building Tomorrow\'s Web Experiences';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Welcome to CyberDev Nexus Command Center - A futuristic developer portfolio showcasing cutting-edge web technologies, innovative projects, and mentorship opportunities in the digital frontier.');
    }

    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';

    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  const pageVariants = {
    initial: { opacity: 0 },
    animate: { 
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0,
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen bg-background text-foreground overflow-x-hidden"
    >
      {/* Header */}
      <Header />

      {/* Main Content */}
      <main className="relative">
        {/* Hero Section */}
        <HeroSection />

        {/* Metrics Display */}
        <MetricsDisplay />

        {/* Project Preview */}
        <ProjectPreview />

        {/* Tech Stack */}
        <TechStack />

        {/* Call to Action */}
        <CallToAction />
      </main>

      {/* Footer */}
      <footer className="relative bg-background border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-brand-cyan to-brand-purple rounded-lg flex items-center justify-center cyber-glow">
                  <span className="font-orbitron font-bold text-sm text-white">CN</span>
                </div>
                <div>
                  <h3 className="font-orbitron font-bold text-xl text-foreground">CyberDev Nexus</h3>
                  <p className="font-mono text-xs text-text-secondary">Building Tomorrow's Web</p>
                </div>
              </div>
              <p className="font-inter text-text-secondary leading-relaxed max-w-md">
                Pushing the boundaries of web development through innovative technologies, 
                immersive experiences, and visionary digital solutions.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-orbitron font-bold text-foreground mb-4">Navigation</h4>
              <ul className="space-y-2">
                <li><a href="/identity-matrix-about" className="font-inter text-text-secondary hover:text-brand-cyan transition-colors duration-300">Identity Matrix</a></li>
                <li><a href="/arsenal-showcase-skills-technologies" className="font-inter text-text-secondary hover:text-brand-cyan transition-colors duration-300">Arsenal</a></li>
                <li><a href="/project-dimensions-portfolio" className="font-inter text-text-secondary hover:text-brand-cyan transition-colors duration-300">Projects</a></li>
                <li><a href="/neural-network-contact-collaboration" className="font-inter text-text-secondary hover:text-brand-cyan transition-colors duration-300">Contact</a></li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 className="font-orbitron font-bold text-foreground mb-4">Connect</h4>
              <div className="flex items-center gap-3">
                <a
                  href="/neural-network-contact-collaboration"
                  className="p-2 rounded-lg text-text-secondary hover:text-brand-cyan hover:bg-surface/40 transition-colors duration-300 cyber-hover"
                  aria-label="Contact"
                  title="Contact"
                >
                  <Icon name="MessageSquare" size={18} />
                </a>
                <a
                  href="https://github.com/Account35?tab=repositories"
                  className="p-2 rounded-lg text-text-secondary hover:text-brand-cyan hover:bg-surface/40 transition-colors duration-300 cyber-hover"
                  aria-label="GitHub"
                  title="GitHub"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="Github" size={18} />
                </a>
                <a
                  href="https://www.linkedin.com/in/lwando-ntlemeza-091842204/"
                  className="p-2 rounded-lg text-text-secondary hover:text-brand-cyan hover:bg-surface/40 transition-colors duration-300 cyber-hover"
                  aria-label="LinkedIn"
                  title="LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="Linkedin" size={18} />
                </a>
                <a
                  href="https://wa.me/27631819011"
                  className="p-2 rounded-lg text-text-secondary hover:text-brand-cyan hover:bg-surface/40 transition-colors duration-300 cyber-hover"
                  aria-label="WhatsApp"
                  title="WhatsApp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="Whatsapp" size={18} />
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-border mt-12 pt-8 flex flex-col md:flex-row items-center justify-between">
            <p className="font-mono text-sm text-text-tertiary">
              © {new Date().getFullYear()} CyberDev Nexus. All rights reserved.
            </p>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-brand-green rounded-full animate-pulse"></div>
                <span className="font-mono text-xs text-text-tertiary">System Online</span>
              </div>
              <span className="font-mono text-xs text-text-tertiary">v2.4.7</span>
            </div>
          </div>
        </div>
      </footer>
    </motion.div>
  );
};

export default NexusCommandCenter;